import { Injectable } from '@angular/core';
import { ApiBaseService } from './api-base.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ErrorService } from '../error/error.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { TimesheetEntrySearchResults, TimesheetExpenseSearchResults, TimesheetSearchResults } from 'src/app/shared/models/timesheet/timesheetSearchResults';
import { Timesheet } from 'src/app/shared/models/timesheet/timesheet';
import { TimesheetEntrySearchParams, TimesheetExpenseSearchParams, TimesheetSearchParams } from 'src/app/shared/models/timesheet/timesheetSearchParams';
import { TimesheetEntry, TimesheetExpense } from 'src/app/shared/models/timesheet/timesheetEntry';

@Injectable({
  providedIn: 'root'
})
export class TimesheetApiService extends ApiBaseService {

  baseUrl = environment.backend_host + '/api/timesheet';

  constructor(
    public http: HttpClient,
    protected errorService: ErrorService,
    protected spinner: NgxSpinnerService) {
    super(spinner, errorService);
  }

  /***********************
  // Timesheets
  /***********************/

  public getTimesheets = (params: TimesheetSearchParams): Observable<TimesheetSearchResults> => {
    const p = this.serializeParamsObj(params, new HttpParams());
    return this.http.get<any>(`${this.baseUrl}/`, { params: p })
      .pipe(map(this.deserializeAs(TimesheetSearchResults)));
  }

  public getTimesheet = (timesheet: Timesheet): Observable<Timesheet> => {
    return this.http.get<any>(`${this.baseUrl}/${timesheet.id}`)
      .pipe(map(this.deserializeAs(Timesheet)));
  }

  public activeTimesheets = (): Observable<Timesheet[]> => {
    return this.http.get<any>(`${this.baseUrl}/lawyer`)
      .pipe(map(this.deserializeArrayAs(Timesheet)));
  }

  /***********************
  // Timesheets
  /***********************/

  public getTimesheetEntries = (params: TimesheetEntrySearchParams): Observable<TimesheetEntrySearchResults> => {
    const p = this.serializeParamsObj(params, new HttpParams());
    return this.http.get<any>(`${this.baseUrl}/entries`, { params: p })
      .pipe(map(this.deserializeAs(TimesheetEntrySearchResults)));
  }

  public getTimesheetEntry = (entry: TimesheetEntry): Observable<TimesheetEntry> => {
    return this.http.get<any>(`${this.baseUrl}/entries/${entry.id}`)
      .pipe(map(this.deserializeAs(TimesheetEntry)));
  }

  public createTimesheetEntry = (entry: TimesheetEntry): Observable<TimesheetEntry> => {
    return this.http.post<any>(`${this.baseUrl}/entries`, this.serialize(entry))
      .pipe(map(this.deserializeAs(TimesheetEntry)));
  }

  public updateTimesheetEntry = (entry: TimesheetEntry): Observable<TimesheetEntry> => {
    return this.http.put<any>(`${this.baseUrl}/entries/${entry.id}`, this.serialize(entry))
      .pipe(map(this.deserializeAs(TimesheetEntry)));
  }

  public deleteTimesheetEntry = (entry: TimesheetEntry): Observable<any> => {
    return this.http.delete<any>(`${this.baseUrl}/entries/${entry.id}`)
      .pipe(share());
  }

  public getOverdueTimesheetEntries = (params: TimesheetEntrySearchParams): Observable<TimesheetEntrySearchResults> => {
    const p = this.serializeParamsObj(params, new HttpParams());
    return this.http.get<any>(`${this.baseUrl}/overdue-timesheets`, { params: p })
      .pipe(map(this.deserializeAs(TimesheetEntrySearchResults)));
  }

  public getTimesheetExpenses = (params: TimesheetExpenseSearchParams): Observable<TimesheetExpenseSearchResults> => {
    const p = this.serializeParamsObj(params, new HttpParams());
    return this.http.get<any>(`${this.baseUrl}/expenses`, { params: p })
      .pipe(map(this.deserializeAs(TimesheetExpenseSearchResults)));
  }

  public getTimesheetExpense = (expense: TimesheetExpense): Observable<TimesheetExpense> => {
    return this.http.get<any>(`${this.baseUrl}/expenses/${expense.id}`)
      .pipe(map(this.deserializeAs(TimesheetExpense)));
  }

  public createTimesheetExpense = (expense: TimesheetExpense): Observable<TimesheetExpense> => {
    return this.http.post<any>(`${this.baseUrl}/expenses`, this.serialize(expense))
      .pipe(map(this.deserializeAs(TimesheetExpense)));
  }

  public updateTimesheetExpense = (expense: TimesheetExpense): Observable<TimesheetExpense> => {
    return this.http.put<any>(`${this.baseUrl}/expenses/${expense.id}`, this.serialize(expense))
      .pipe(map(this.deserializeAs(TimesheetExpense)));
  }

  public deleteTimesheetExpense = (expense: TimesheetExpense): Observable<any> => {
    return this.http.delete<any>(`${this.baseUrl}/expenses/${expense.id}`)
      .pipe(share());
  }
}
